/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import * as signalR from '@microsoft/signalr'
import { useAuth } from '../hooks/useAuth'

const WebSocketContext = createContext(null)

// Module-level singleton tracker (survives StrictMode remounts)
let globalConnectionInstance = null
let globalUserId = null

/**
 * WebSocket Provider - Singleton connection management
 *
 * This provider ensures only ONE WebSocket connection is created per user session,
 * preventing 429 rate limit errors from Azure SignalR.
 *
 * Features:
 * - Singleton connection (shared across all components)
 * - Auth token from AuthContext (not localStorage)
 * - Subscription queue (if connection not ready)
 * - Proper cleanup on unmount
 * - StrictMode protection (ref-based)
 */
export function WebSocketProvider({ children, hubUrl }) {
  const { user, getAccessToken } = useAuth()
  const [connection, setConnection] = useState(null)
  const [isConnected, setIsConnected] = useState(false)

  // Refs for connection management
  const connectionRef = useRef(null)
  const retryCountRef = useRef(0)
  const retryTimeoutRef = useRef(null)
  const isCleaningUpRef = useRef(false)

  // Queue for subscriptions that happen before connection is ready
  const subscriptionQueueRef = useRef([])
  const isProcessingQueueRef = useRef(false)

  useEffect(() => {
    // No user = no connection needed
    if (!user) {
      console.log('[WebSocketContext] No user - skipping initialization')
      return
    }

    // Check if we already have a connection for this user (module-level singleton)
    if (globalConnectionInstance && globalUserId === user.id) {
      console.log('[WebSocketContext] Reusing existing connection for user:', user.id)
      // Reuse existing connection
      connectionRef.current = globalConnectionInstance
      setConnection(globalConnectionInstance)
      setIsConnected(globalConnectionInstance.state === signalR.HubConnectionState.Connected)
      return
    }

    // User changed - cleanup old connection
    if (globalConnectionInstance && globalUserId !== user.id) {
      console.log('[WebSocketContext] User changed - cleaning up old connection')
      globalConnectionInstance.stop().catch(console.error)
      globalConnectionInstance = null
      globalUserId = null
    }

    // Create new connection (true singleton - only happens once per user)
    console.log('[WebSocketContext] Creating NEW singleton connection for user:', user.id)
    globalUserId = user.id
    isCleaningUpRef.current = false
    retryCountRef.current = 0

    // SignalR base URL for Azure Functions
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:7071/api'
    const url = hubUrl || baseUrl

    console.log('[WebSocketContext] SignalR base URL:', url)

    // Create connection - let SignalR library handle negotiation completely
    // The library will call /negotiate, get the redirect response, and handle the SignalR Service connection
    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        skipNegotiation: false, // Let SignalR do negotiation
        accessTokenFactory: async () => {
          // Provide Supabase auth token for OUR /negotiate endpoint
          // The library will get SignalR Service token from the redirect response
          const token = await getAccessToken()
          if (!token) {
            console.error('[WebSocketContext] No access token available')
          }
          return token
        },
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build()

    // Store in both module-level and ref
    globalConnectionInstance = newConnection
    connectionRef.current = newConnection

    // Setup event handlers
    newConnection.onreconnecting((error) => {
      console.log('[WebSocketContext] Reconnecting:', error)
      setIsConnected(false)
    })

    newConnection.onreconnected((connectionId) => {
      console.log('[WebSocketContext] Reconnected:', connectionId)
      setIsConnected(true)
      retryCountRef.current = 0

      // Process any queued subscriptions
      processSubscriptionQueue(newConnection)
    })

    newConnection.onclose((error) => {
      console.log('[WebSocketContext] Closed:', error)
      setIsConnected(false)
    })

    // Start connection with limited retries and exponential backoff
    const startConnection = async () => {
      // Stop if component is unmounting or user logged out
      if (isCleaningUpRef.current || !user) {
        return
      }

      // Max 5 retry attempts
      if (retryCountRef.current >= 5) {
        console.error('[WebSocketContext] Max retry attempts reached. Stopping connection attempts.')
        return
      }

      try {
        await newConnection.start()
        console.log('[WebSocketContext] Connected successfully')
        setIsConnected(true)
        setConnection(newConnection)
        retryCountRef.current = 0

        // Process any queued subscriptions
        processSubscriptionQueue(newConnection)
      } catch (err) {
        console.error('[WebSocketContext] Connection failed:', err)

        // Don't retry on 429 (rate limit) errors
        if (err.message?.includes('429') || err.message?.includes('Too Many Requests')) {
          console.error('[WebSocketContext] Rate limit reached. This should not happen with singleton connection!')
          return
        }

        // Exponential backoff: 2s, 4s, 8s, 16s, 32s
        retryCountRef.current++
        const delay = Math.min(2000 * Math.pow(2, retryCountRef.current - 1), 32000)

        console.log(`[WebSocketContext] Retry attempt ${retryCountRef.current}/5 in ${delay}ms`)

        if (!isCleaningUpRef.current && user) {
          retryTimeoutRef.current = setTimeout(startConnection, delay)
        }
      }
    }

    startConnection()

    // Cleanup on unmount or user logout
    return () => {
      // On user logout, clean up the global connection
      if (!user && globalConnectionInstance) {
        console.log('[WebSocketContext] User logged out - stopping global connection')
        globalConnectionInstance.stop().catch(console.error)
        globalConnectionInstance = null
        globalUserId = null
      } else if (!user) {
        console.log('[WebSocketContext] Cleanup with no user (already cleaned)')
      } else {
        console.log('[WebSocketContext] StrictMode cleanup - preserving global connection')
      }

      // Clear component-level refs
      isCleaningUpRef.current = true

      // Clear any pending retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }

      // Clear subscription queue
      subscriptionQueueRef.current = []

      // Clear component state (but keep global connection)
      connectionRef.current = null
      setConnection(null)
      setIsConnected(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hubUrl]) // Removed getAccessToken to prevent memory leak (it's called inside accessTokenFactory)

  // Process queued subscriptions when connection becomes ready
  const processSubscriptionQueue = (conn) => {
    // Prevent concurrent processing
    if (isProcessingQueueRef.current) {
      console.log('[WebSocketContext] Queue processing already in progress, skipping')
      return
    }

    const queue = subscriptionQueueRef.current
    if (queue.length === 0) {
      return
    }

    try {
      isProcessingQueueRef.current = true
      console.log(`[WebSocketContext] Processing ${queue.length} queued subscriptions`)

      queue.forEach(({ eventName, callback }) => {
        try {
          conn.on(eventName, callback)
          console.log(`[WebSocketContext] Subscribed to queued event: ${eventName}`)
        } catch (err) {
          console.error(`[WebSocketContext] Failed to subscribe to queued event ${eventName}:`, err)
        }
      })

      // Clear the queue
      subscriptionQueueRef.current = []
    } finally {
      isProcessingQueueRef.current = false
    }
  }

  /**
   * Subscribe to a WebSocket event
   * If connection is not ready, subscription will be queued
   */
  const subscribe = useCallback((eventName, callback) => {
    if (!eventName || !callback) {
      console.warn('[WebSocketContext] Invalid subscribe call: missing eventName or callback')
      return () => {} // Return no-op unsubscribe
    }

    const conn = connectionRef.current

    // Check SignalR connection state directly (not React state)
    if (conn?.state === signalR.HubConnectionState.Connected) {
      // Connection ready - subscribe immediately
      try {
        conn.on(eventName, callback)
        console.log(`[WebSocketContext] Subscribed to event: ${eventName}`)

        // Return unsubscribe function
        return () => {
          if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
            try {
              connectionRef.current.off(eventName, callback)
              console.log(`[WebSocketContext] Unsubscribed from event: ${eventName}`)
            } catch (err) {
              console.error(`[WebSocketContext] Failed to unsubscribe from event ${eventName}:`, err)
            }
          }
        }
      } catch (err) {
        console.error(`[WebSocketContext] Failed to subscribe to event ${eventName}:`, err)
        return () => {}
      }
    }

    // Connection not ready - queue the subscription
    console.log(`[WebSocketContext] Connection not ready, queuing subscription for: ${eventName}`)
    subscriptionQueueRef.current.push({ eventName, callback })

    // Return unsubscribe function that removes from queue
    return () => {
      subscriptionQueueRef.current = subscriptionQueueRef.current.filter(
        (sub) => sub.eventName !== eventName || sub.callback !== callback
      )
      console.log(`[WebSocketContext] Removed queued subscription: ${eventName}`)
    }
  }, []) // No dependencies - uses refs only

  /**
   * Send a message to the server
   */
  const send = useCallback(async (methodName, ...args) => {
    const conn = connectionRef.current

    if (conn?.state === signalR.HubConnectionState.Connected) {
      try {
        await conn.invoke(methodName, ...args)
        console.log(`[WebSocketContext] Sent message: ${methodName}`)
        return { success: true }
      } catch (err) {
        console.error(`[WebSocketContext] Failed to send message ${methodName}:`, err)
        return { success: false, error: err.message }
      }
    }

    const error = 'WebSocket is not connected'
    console.error(`[WebSocketContext] ${error}`)
    return { success: false, error }
  }, []) // No dependencies - uses refs only

  const value = {
    connection,
    isConnected,
    subscribe,
    send,
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

/**
 * Hook to access WebSocket context
 * Use this hook in components that need WebSocket functionality
 */
export function useWebSocket() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
